import { Box, Grid, Skeleton } from '@mui/material';
import React from 'react';
import RunningSparkSubHeader from '../RunningSparkSubHeader';
import { useBoardNodesByBoardID } from 'hooks/Board';
import { useSparksByID } from 'hooks/spark';
import { useCurrentRoute } from 'hooks/common';
import {
  InfoCard,
  SparkEditableText,
  SparkScrollableGridContainer,
} from 'sharedComponents/UI';
import { motion } from 'framer-motion';
import { IconsSkeleton } from 'sharedComponents/Skeleton/SparkPopupHeaderSkeleton';
export const DUMMY_NODES = [
  {
    id: '1',
    attributes: {
      title: 'Untitled',
    },
  },
  {
    id: '2',
    attributes: {
      title: 'Untitled',
    },
  },
  {
    id: '3',
    attributes: {
      title: 'Untitled',
    },
  },
];
type SparkRunningProgressScreenProps = {
  nodeOnly?: boolean;
  boardID?: string;
  showHeader?: boolean;
  sparkID: string;
  animate?: boolean;
};

export const BoardNodeTollBarShimmer = () => {
  return (
    <Box
      height='4rem'
      display='flex'
      alignItems='center'
      justifyContent='space-between'
    >
      <Skeleton
        variant='rounded'
        height={40}
        width={62}
        sx={{ borderRadius: '10px' }}
      ></Skeleton>
      <Skeleton
        variant='rounded'
        height={40}
        width={40}
        sx={{ borderRadius: '10px' }}
      ></Skeleton>
    </Box>
  );
};

export const BoardNodeShimmer = ({
  title,
  id,
  key,
  index,
  animate = false,
  styleOverride = {},
}: any) => {
  const { isSparkRun } = useCurrentRoute();
  const getAnimationStyles = (index: number) => {
    if (!animate) {
      return {};
    }
    return {
      initial: { opacity: 0, translateY: -10, scale: 0.9 },
      animate: { opacity: 1, translateY: 0, scale: 1 },
      transition: {
        delay: (index + 1) * 0.7,
        duration: 1,
        ease: 'easeOut',
      },
    };
  };
  return (
    <Grid
      item
      bgcolor='white'
      borderRadius='10px'
      padding={2}
      paddingTop={1}
      component={motion.div}
      margin='1px'
      key={key}
      sx={{
        outline: (theme) => `1px solid ${theme.palette.borderColor}`,
        maxWidth: '800px',
        width: '100%',
        ...styleOverride,
      }}
      {...getAnimationStyles(index)}
    >
      {!isSparkRun ? (
        <Skeleton height={35} width='10%'></Skeleton>
      ) : (
        <SparkEditableText
          initialText={title}
          disabled={true}
          onTextChange={() => {}}
          placeholder='Untitled'
        />
      )}
      <Skeleton height={35} width='100%'></Skeleton>
      <Skeleton height={35} width='100%'></Skeleton>
      <Skeleton height={35} width='100%'></Skeleton>
      <Skeleton height={35} width='100%'></Skeleton>
    </Grid>
  );
};

function SparkRunningProgressScreen({
  nodeOnly = false,
  boardID,
  showHeader = false,
  sparkID,
  animate = true,
}: SparkRunningProgressScreenProps) {
  const { spark } = useSparksByID(boardID ? undefined : sparkID);
  const { boardNodes = [] } = useBoardNodesByBoardID(
    spark?.attributes.outputBoardID || boardID
  );
  const height = React.useMemo(() => {
    if (nodeOnly) {
      return 'calc(100vh - 6rem)';
    }
    return 'calc(100vh - 12rem)';
  }, [nodeOnly]);

  const shimmerNodes = React.useMemo(() => {
    if (!boardNodes.length) {
      return DUMMY_NODES;
    }
    return boardNodes;
  }, [boardNodes]);

  return (
    <>
      {showHeader && (
        <Grid
          container
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          bgcolor='white'
          height='6rem'
        >
          <Grid
            display='flex'
            sx={{
              alignItems: 'center',
            }}
            marginX={2}
            alignItems='center'
            gap={3}
          >
            <Skeleton
              variant='rounded'
              width={45}
              height={45}
              sx={{ borderRadius: '14px' }}
            />
            <Skeleton
              variant='rounded'
              width={75}
              height={75}
              sx={{ borderRadius: '14px' }}
            />
            <Box>
              <Skeleton variant='text' width={170} height={40} />
              <Skeleton
                variant='text'
                width={100}
                height={30}
                sx={{ marginLeft: 1 }}
              />
            </Box>
          </Grid>

          <Grid
            item
            container
            flex={1}
            gap={0.8}
            justifyContent='flex-end'
            flexWrap='nowrap'
            marginX={2}
          >
            <IconsSkeleton />
            <IconsSkeleton />
            <IconsSkeleton />
          </Grid>
        </Grid>
      )}
      <Grid container direction='column' alignItems='center' gap={2}>
        {!nodeOnly && (
          <Box
            component={motion.div}
            initial={{ opacity: 0, translateY: -50, scale: 0.9 }}
            animate={{ opacity: 1, translateY: 0, scale: 1 }}
            transition={{
              duration: 0.5,
              ease: 'easeOut',
            }}
            width='100%'
          >
            <RunningSparkSubHeader />
          </Box>
        )}
        <SparkScrollableGridContainer
          item
          height={height}
          width={!nodeOnly ? '50%' : '100%'}
          style={{ maxWidth: '800px', width: '100%' }}
          gap={2}
          header={
            nodeOnly ? null : (
              <Box width='100%' position='sticky'>
                <InfoCard info={`We'll notify you when the run completes`} />
              </Box>
            )
          }
          scrollableContainerStyle={{
            scrollbarWidth: 'none',
            padding: 0,
            gap: 2,
            marginTop: 2,
          }}
          headerStyle={{ borderBottom: 'none' }}
        >
          {shimmerNodes.map((node, index) => (
            <BoardNodeShimmer
              title={node.attributes.title}
              id={node.id}
              key={`board-node-shimmer-${index}`}
              index={index}
              animate={animate}
            />
          ))}
        </SparkScrollableGridContainer>
      </Grid>
    </>
  );
}

export default SparkRunningProgressScreen;
